"use client";

import { useState, useEffect, useCallback, useRef, lazy, Suspense } from "react";
import { toast } from "sonner";
import type { LucideIcon } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Wrench,
  Calendar,
  CalendarClock,
  Loader2,
  ExternalLink,
  Info,
  ChevronDown,
  Settings2,
  Settings,
  ShoppingBag,
  Mail,
  Truck,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { ToolInstructionsDialog } from "@/components/agents/shared/dialogs/tool-instructions-dialog";
import { CrmStatusFallbackSettings } from "@/components/deliveries/crm-status-fallback-settings";
import { ToolWorkingHoursSetting } from "@/components/agent-tasks/tool-working-hours-setting";
import { ShopDomainField } from "@/components/agents/shared/shop-domain-field";
import {
  ORDER_LOOKUP_LOCKED_REASON,
  SHOPIFY_TOOL_TILE_DEFS,
  type ShopifyToolTileDef,
} from "@/components/agents/shared/shopify-tool-tile";
import { ShopifyLiveDataNote } from "@/components/agents/shared/shopify-live-data-note";
import {
  WorkingHoursConfigEditor,
  MeetingDefaultsConfig,
} from "@/components/agents/shared/calendar";
import { OAuthScopeConnectionStatus } from "@/components/integrations/oauth";
import { CrmToolsGroup } from "@/components/agents/shared/crm-tools-group";
import {
  AgentToolCard,
  AgentToolCategory,
  type AgentToolCategoryTone,
} from "@/components/agents/shared/agent-tool-presentation";
import { CRM_OPPORTUNITY_TOOL_NAME } from "@/lib/ai/tools/crm-opportunity/client-types";
import { useVoiceAgentConfig } from "@/hooks/agents/voice";
import { useShopifyIntegration } from "@/hooks/integrations/use-shopify-integration";
import { useShopifyStorefrontToolsAccess } from "@/hooks/use-shopify-storefront-tools-access";
import { useCalendarIntegration, useGoogleIntegration } from "@/hooks/integrations/google";
import { useCrmBinding } from "@/hooks/integrations/use-crm-binding";
import { useAgentDeliverySettingsQuery } from "@/hooks/queries/delivery-bindings";
import { useAuth } from "@/hooks/use-auth";
import { isDeliveryToolName } from "@/lib/deliveries/config";
import { DELIVERY_TOOL_SELECTION_HINT, getDeliveryToolRequirement, toggleDeliveryToolSelection } from "@/lib/deliveries/tool-selection";
import { getAiToolDisplayName } from "@/lib/ai/tools/display-name";
import { SettingsTabLoader } from "./settings-tab-loader";
import type { VoiceAgentConfig } from "@/lib/voice/agent-config";
import { getVoiceScheduledSendSetupMessage } from "@/lib/voice/scheduled-send-tool-config";

const ScheduleMessageToolSettings = lazy(() => import("./schedule-message-tool-settings")
  .then(module => ({ default: module.ScheduleMessageToolSettings })));
const ScheduleEmailToolSettings = lazy(() => import("@/components/agent-tasks/schedule-email-tool-settings")
  .then(module => ({ default: module.ScheduleEmailToolSettings })));

interface VoiceToolInfo {
  name: string;
  display_name: string;
  description: string;
  enabled: boolean;
}

// Storefront MCP tools (Catalog + Policies) — gated on any active Shopify
// integration. Pinning the shop domain re-syncs these (they read the
// pinned store), so they're tracked separately from Order Lookup.
const SHOPIFY_STOREFRONT_TOOLS = new Set([
  "search_shopify_catalog",
  "search_shopify_policies",
]);

// All Shopify storefront-adjacent tools rendered in the grouped Shopify
// section (excluded from the generic toggle loop).
const SHOPIFY_TOOLS = new Set<string>([
  "get_shopify_order",
  "search_shopify_catalog",
  "search_shopify_policies",
]);

const CALENDAR_TOOLS = new Set([
  "check_calendar_availability",
  "book_calendar_meeting",
]);

const SCHEDULE_TOOLS = new Set([
  "schedule_call",
  "send_email",
  "send_whatsapp",
]);

// CRM tools are managed via the shared CrmToolsGroup (whitelist + snapshot
// gating lives in the crm-tools settings route) — they are EXCLUDED from
// the generic toggle cards, but stay part of the declarative enabled set
// the generic POST sends (so toggling another tool never disables them).
const CRM_TOOLS = new Set<string>([
  CRM_OPPORTUNITY_TOOL_NAME.updateFields,
  CRM_OPPORTUNITY_TOOL_NAME.moveStage,
]);

// Sentinel `togglingTool` value while the calendar master switch (which flips
// BOTH calendar tools at once) is in flight — keeps every switch disabled like
// a single-tool toggle does.
const CALENDAR_TOGGLE_KEY = "__calendar_booking__";

// Collapsible open-state key for the Calendar Booking card (shares the
// generic-tool `openToolCards` set so the card behaves like a generic tool).
const CALENDAR_CARD_KEY = "__calendar_card__";

// Short, human-facing card descriptions for the generic tools. The raw
// `tool.description` is the LLM-facing instruction (verbose and prescriptive),
// which reads oddly as UI copy — override known tools with a concise summary.
const GENERIC_TOOL_UI_DESCRIPTIONS: Record<string, string> = {
  capture_customer_info:
    "Captures information that identifies customers, relates conversations to the same customer, and keeps CRM records helpful.",
  schedule_call:
    "Lets callers arrange a callback from this Voice Agent at an agreed future time.",
};

interface ToolsSettingsProps {
  agentId: string;  // UUID
}

interface VoiceToolCardsProps {
  agentId?: string;
  tools: VoiceToolInfo[];
  tone: AgentToolCategoryTone;
  categoryLabel: string;
  togglingTool: string | null;
  openToolCards: Set<string>;
  onToggleTool: (toolName: string) => Promise<void>;
  onToggleCard: (toolName: string) => void;
  onOpenInstructions: (toolName: string) => void;
  lockedReason?: string;
  config?: VoiceAgentConfig;
  saveConfig?: (config: Partial<VoiceAgentConfig>) => Promise<boolean>;
  onSettingsSaved?: (change?: { toolName: string; enabled: boolean }) => Promise<{ ok: boolean; error?: string }>;
}

export function ToolsSettings({ agentId }: ToolsSettingsProps) {
  const { fetchWithAuth, user } = useAuth();
  const {
    config,
    isLoading: isLoadingConfig,
    isSaving: isSavingConfig,
    saveConfig,
  } = useVoiceAgentConfig(agentId);
  const { integrations: shopifyIntegrations } = useShopifyIntegration();
  // Storefront MCP tools (Catalog + Policies) need ≥1 active Shopify
  // integration — channel-agnostic, drives whole Shopify-group visibility.
  const { allowed: hasStorefrontAccess } = useShopifyStorefrontToolsAccess();
  // Order Lookup additionally needs an API-enabled integration; when missing
  // (but the group is still visible) the tile is locked, not hidden.
  const hasOrderLookupAccess = shopifyIntegrations.some(
    (i) => i.is_active && i.uses_api,
  );
  const {
    config: calendarConfig,
    inboxId: calendarInboxId,
    inboxes: calendarInboxes,
    isLoading: isLoadingCalendar,
    isSaving: isSavingCalendar,
    error: calendarError,
    loadConfig: loadCalendarConfig,
    updateConfig: updateCalendarConfig,
    connectCalendar,
  } = useCalendarIntegration({ agentId, agentType: "voice" });
  // Neutral, tenant-level Google account connect (no agent attach) — voice has
  // no Inbox tab, so the Calendar Booking card provisions a new account inline.
  const { connectGoogle, isConnecting: isConnectingGoogle } =
    useGoogleIntegration();
  // CRM effective availability — mirror the resolver's agent→tenant-default
  // fallback so the group is hidden only when no CRM resolves for this agent.
  const {
    binding: crmBinding,
    selectedIntegration: crmSelectedIntegration,
    hasTenantDefault: hasCrmTenantDefault,
    tenantDefault: crmTenantDefault,
    tenantDefaultIntegration: crmTenantDefaultIntegration,
  } = useCrmBinding({ scopeType: "voice_agent", scopeId: agentId });
  const deliverySettingsQuery = useAgentDeliverySettingsQuery(
    "voice_agent",
    agentId,
  );

  const [tools, setTools] = useState<VoiceToolInfo[]>([]);
  const [isLoadingTools, setIsLoadingTools] = useState(true);
  const [togglingTool, setTogglingTool] = useState<string | null>(null);
  const [syncMessage, setSyncMessage] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  // Tool instructions dialog state
  const [instructionsDialogOpen, setInstructionsDialogOpen] = useState(false);
  const [editingToolName, setEditingToolName] = useState<string | null>(null);

  const loadTools = useCallback(async () => {
    setIsLoadingTools(true);
    try {
      const response = await fetchWithAuth(
        `/api/agents/voice/${agentId}/tools`,
      );
      if (response.ok) {
        const data = await response.json();
        setTools((data.tools || []) as VoiceToolInfo[]);
      }
    } catch (err) {
      console.error("Error loading AI tools:", err);
    } finally {
      setIsLoadingTools(false);
    }
  }, [fetchWithAuth, agentId]);

  useEffect(() => {
    loadTools();
  }, [loadTools]);

  const enabledTools = tools.filter((t) => t.enabled).map((t) => t.name);

  // Sync the full enabled set to the backend. The POST both persists the
  // per-agent enabled state and pushes the tool set to ElevenLabs.
  const syncTools = useCallback(
    async (newEnabled: string[]): Promise<{ ok: boolean; skipped?: string[]; error?: string }> => {
      try {
        const response = await fetchWithAuth(`/api/agents/voice/${agentId}/tools`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ enabled_tools: newEnabled }),
        });
        if (!response.ok) {
          const body: unknown = await response.json().catch(() => null);
          const error = body && typeof body === "object" && "error" in body
            && typeof body.error === "string" ? body.error : "Failed to sync tools";
          toast.error(error, { duration: 12000 });
          return { ok: false, error };
        }
        const data = await response.json();
        return { ok: true, skipped: data.skipped_tools as string[] | undefined };
      } catch (err) {
        console.error("Error syncing tools:", err);
        toast.error("Failed to sync tools. Try again.");
        return { ok: false };
      }
    },
    [fetchWithAuth, agentId],
  );

  function syncSavedSettings(change?: { toolName: string; enabled: boolean }) {
    if (!change) return syncTools(enabledTools);
    const nextEnabled = enabledTools.filter(name => name !== change.toolName);
    if (change.enabled) nextEnabled.push(change.toolName);
    return syncTools(nextEnabled);
  }

  const handleToggleTool = async (toolName: string) => {
    if ((toolName === "send_email" || toolName === "send_whatsapp")
      && !tools.find(tool => tool.name === toolName)?.enabled) {
      const setupMessage = getVoiceScheduledSendSetupMessage(config, toolName);
      if (setupMessage) {
        setOpenToolCards(prev => new Set(prev).add(toolName));
        setSyncMessage({ type: "error", message: setupMessage });
        return;
      }
    }
    setTogglingTool(toolName);

    const wasEnabled = enabledTools.includes(toolName);
    let newEnabled = wasEnabled
      ? enabledTools.filter((name) => name !== toolName)
      : [...enabledTools, toolName];
    if (isDeliveryToolName(toolName)) {
      newEnabled = toggleDeliveryToolSelection(enabledTools, toolName, !wasEnabled);
    }

    if (wasEnabled && ["schedule_call", "send_email", "send_whatsapp"].includes(toolName) && saveConfig) {
      const saved = await saveConfig({
        tools_enabled: { ...config?.tools_enabled, [toolName]: false },
        ai_tools: { ...config?.ai_tools, tool_settings: { ...config?.ai_tools?.tool_settings, [toolName]: {} } },
      });
      if (!saved) {
        setSyncMessage({ type: "error", message: "Could not clear tool settings." });
        setTogglingTool(null);
        return;
      }
    }

    // Optimistic update
    setTools((prev) =>
      prev.map((t) => ({ ...t, enabled: newEnabled.includes(t.name) })),
    );

    const result = await syncTools(newEnabled);

    if (result.ok) {
      if (isDeliveryToolName(toolName)) void deliverySettingsQuery.refetch();
      const skippedThisTool = result.skipped?.includes(toolName);
      setSyncMessage(
        skippedThisTool
          ? {
              type: "error",
              message: CALENDAR_TOOLS.has(toolName)
                ? "Tool enabled, but it needs a calendar inbox. Select an inbox in Calendar Booking to activate it."
                : "Tool enabled, but it requires an active Shopify integration to work. Connect Shopify to activate it.",
            }
          : {
              type: "success",
              message: `Tool ${!wasEnabled ? "enabled" : "disabled"} successfully`,
            },
      );
    } else {
      // Rollback on failure
      setTools((prev) =>
        prev.map((t) => ({ ...t, enabled: enabledTools.includes(t.name) })),
      );
      setSyncMessage({ type: "error", message: result.error ?? "Failed to sync tools" });
    }
    setTimeout(() => setSyncMessage(null), 5000);

    setTogglingTool(null);
  };

  // Calendar Booking master switch — flips BOTH calendar tools at once. Stays
  // enabled even without a usable inbox (intent-before-configure contract):
  // the backend skips delivery and reports `skipped_tools`, surfaced as a
  // "needs a calendar inbox" warning here.
  const calendarToolNames = tools
    .filter((t) => CALENDAR_TOOLS.has(t.name))
    .map((t) => t.name);
  const calendarBookingEnabled = calendarToolNames.some((name) =>
    enabledTools.includes(name),
  );

  const handleToggleCalendarBooking = async () => {
    if (calendarToolNames.length === 0) return;
    setTogglingTool(CALENDAR_TOGGLE_KEY);

    const wasEnabled = calendarBookingEnabled;
    const newEnabled = wasEnabled
      ? enabledTools.filter((name) => !CALENDAR_TOOLS.has(name))
      : Array.from(new Set([...enabledTools, ...calendarToolNames]));

    // Optimistic update of both calendar tools
    setTools((prev) =>
      prev.map((t) =>
        CALENDAR_TOOLS.has(t.name) ? { ...t, enabled: !wasEnabled } : t,
      ),
    );

    const result = await syncTools(newEnabled);

    if (result.ok) {
      const anySkipped = calendarToolNames.some((name) =>
        result.skipped?.includes(name),
      );
      setSyncMessage(
        anySkipped
          ? {
              type: "error",
              message:
                "Calendar booking enabled, but it needs a calendar inbox. Select an inbox below to activate it.",
            }
          : {
              type: "success",
              message: `Calendar booking ${!wasEnabled ? "enabled" : "disabled"} successfully`,
            },
      );
    } else {
      // Rollback both
      setTools((prev) =>
        prev.map((t) =>
          CALENDAR_TOOLS.has(t.name) ? { ...t, enabled: wasEnabled } : t,
        ),
      );
      setSyncMessage({ type: "error", message: result.error ?? "Failed to sync tools" });
    }
    setTimeout(() => setSyncMessage(null), 5000);

    setTogglingTool(null);
  };

  const handleShopDomainChange = async (domain: string | null) => {
    const saved = await saveConfig({ shopify_shop_domain: domain });
    if (saved && enabledTools.some((name) => SHOPIFY_STOREFRONT_TOOLS.has(name))) {
      // Re-sync so the pinned domain reaches the delivered tool config.
      await syncTools(enabledTools);
    }
  };

  const handleCalendarInboxChange = async (inboxId: string) => {
    try {
      await updateCalendarConfig({ inbox_id: inboxId || null });
    } catch {
      return;
    }
    if (enabledTools.some((name) => CALENDAR_TOOLS.has(name))) {
      // Re-sync: calendar tools are delivered only with a configured inbox.
      await syncTools(enabledTools);
    }
  };

  // Open instructions dialog for a tool
  const openInstructionsDialog = (toolName: string) => {
    setEditingToolName(toolName);
    setInstructionsDialogOpen(true);
  };

  // Handle when instructions are saved - re-sync tools to apply new instructions
  const handleInstructionsSaved = async (toolName: string) => {
    if (!enabledTools.includes(toolName)) {
      // Tool is disabled, no need to sync - new instructions will be applied when enabled
      return;
    }

    setTogglingTool(toolName);

    // Just re-sync with current enabled tools - the API will fetch fresh instructions from DB
    const result = await syncTools(enabledTools);

    setSyncMessage(
      result.ok
        ? {
            type: "success",
            message: `Instructions updated and synced for ${getDisplayName(toolName)}`,
          }
        : {
            type: "error",
            message:
              "Failed to sync updated instructions. Please try toggling the tool off and on.",
          },
    );

    setTogglingTool(null);
    setTimeout(() => setSyncMessage(null), 5000);
  };

  const getDisplayName = (toolName: string): string =>
    getAiToolDisplayName(
      toolName,
      tools.find((tool) => tool.name === toolName)?.display_name,
    );

  // Collapsible state - collapsed by default
  const [openToolCards, setOpenToolCards] = useState<Set<string>>(new Set());

  const toggleToolCard = (toolName: string) => {
    setOpenToolCards((prev) => {
      const next = new Set(prev);
      if (next.has(toolName)) {
        next.delete(toolName);
      } else {
        next.add(toolName);
      }
      return next;
    });
  };

  const hasCalendarTools = tools.some((t) => CALENDAR_TOOLS.has(t.name));
  const hasCrmTools = tools.some((t) => CRM_TOOLS.has(t.name));

  // Shopify tiles present in this agent's catalog (resolver returns the full
  // catalog, so normally all three) — render only present ones to avoid
  // toggling tool names the backend doesn't know.
  const presentShopifyTiles = SHOPIFY_TOOL_TILE_DEFS.filter((def) =>
    tools.some((t) => t.name === def.name),
  );
  // Order Lookup is force-locked (shown OFF) without an API-enabled
  // integration — exclude such locked tiles from the header count so it never
  // claims a tool is enabled while the tile reads OFF.
  const isShopifyTileLocked = (def: ShopifyToolTileDef) =>
    Boolean(def.requiresApi && !hasOrderLookupAccess);
  // Effective CRM availability — mirrors the resolver's agent→tenant-default
  // fallback while respecting an explicitly-disabled agent binding. An agent
  // binding always wins (even when disabled → hide); otherwise fall back to an
  // enabled tenant default with an active integration.
  const crmAvailable = crmBinding
    ? crmBinding.config?.enabled !== false && Boolean(crmSelectedIntegration?.isActive)
    : hasCrmTenantDefault &&
      crmTenantDefault?.enabled !== false &&
      Boolean(crmTenantDefaultIntegration?.isActive);

  // Generic toggle cards exclude grouped tools (Shopify / calendar / CRM are
  // rendered in their own sections above).
  const genericTools = tools.filter(
    (t) =>
      !SHOPIFY_TOOLS.has(t.name) &&
      !CALENDAR_TOOLS.has(t.name) &&
      !CRM_TOOLS.has(t.name),
  );
  const deliveryTools = deliverySettingsQuery.data?.deliveryEnabled
    ? genericTools.filter(tool => isDeliveryToolName(tool.name))
    : [];
  const deliveryLockedReason = getDeliveryLockedReason(
    deliverySettingsQuery.data,
  );
  const generalTools = genericTools.filter(tool => !isDeliveryToolName(tool.name) && !SCHEDULE_TOOLS.has(tool.name));
  const scheduleTools = genericTools.filter(tool => SCHEDULE_TOOLS.has(tool.name));
  const selectedCalendarInbox = calendarInboxes.find((i) => i.id === calendarInboxId);

  // Calendar tools are delivered only when the selected inbox has calendar
  // scope. Granting access happens in an OAuth popup that refreshes the
  // inbox list — when scope flips to granted, re-sync so the enabled
  // calendar tools actually reach the agent without another manual save.
  const prevCalendarScopeRef = useRef<boolean | undefined>(undefined);
  const selectedCalendarScope = selectedCalendarInbox?.hasCalendarScope;
  useEffect(() => {
    const prev = prevCalendarScopeRef.current;
    prevCalendarScopeRef.current = selectedCalendarScope;
    if (prev === false && selectedCalendarScope === true) {
      const enabledNow = tools.filter((t) => t.enabled).map((t) => t.name);
      if (enabledNow.some((name) => CALENDAR_TOOLS.has(name))) {
        void syncTools(enabledNow);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- transition-edge effect: only the scope flip matters
  }, [selectedCalendarScope]);

  // Show loader until config and tools are loaded
  if (isLoadingConfig || isLoadingTools || deliverySettingsQuery.isLoading) {
    return <SettingsTabLoader message="Loading tools…" />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground">AI Tools</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Enable tools for integrations, call management, and external data access.
        </p>
      </div>

      {/* Status Message */}
      {syncMessage && (
        <Alert
          aria-live="polite"
          className={
            syncMessage.type === "success"
              ? "border-green-200 bg-green-50"
              : "border-red-200 bg-red-50"
          }
        >
          <AlertDescription
            className={
              syncMessage.type === "success"
                ? "text-green-800"
                : "text-red-800"
            }
          >
            {syncMessage.message}
          </AlertDescription>
        </Alert>
      )}

      {/* Keep CRM application tools together before delivery and general tools. */}
      {hasCrmTools && crmAvailable ? (
        <CrmToolsGroup
          channel="voice"
          agentId={agentId}
          canManage={true}
          onOpenUpdateFieldsInstructions={() =>
            openInstructionsDialog(CRM_OPPORTUNITY_TOOL_NAME.updateFields)
          }
          onOpenMoveStageInstructions={() =>
            openInstructionsDialog(CRM_OPPORTUNITY_TOOL_NAME.moveStage)
          }
          onConfigChanged={loadTools}
        />
      ) : null}

      {deliveryTools.length > 0 ? (
        <AgentToolCategory
          title="Delivery Tools"
          description={DELIVERY_TOOL_SELECTION_HINT}
          tone="delivery"
          icon={Truck}
        >
          <VoiceToolCards
            tools={deliveryTools}
            tone="delivery"
            categoryLabel="Delivery"
            togglingTool={togglingTool}
            openToolCards={openToolCards}
            onToggleTool={handleToggleTool}
            onToggleCard={toggleToolCard}
            onOpenInstructions={openInstructionsDialog}
            lockedReason={deliveryLockedReason}
            agentId={agentId}
          />
        </AgentToolCategory>
      ) : null}

      {generalTools.length > 0 || hasCalendarTools
        || (hasStorefrontAccess && presentShopifyTiles.length > 0) ? (
        <AgentToolCategory
          title="General Tools"
          description="Use customer identity, calendar, commerce, and other connected capabilities during calls."
          tone="general"
          icon={Wrench}
        >
          <VoiceToolCards
            tools={generalTools}
            tone="general"
            categoryLabel="General"
            config={config}
            saveConfig={saveConfig}
            onSettingsSaved={syncSavedSettings}
            togglingTool={togglingTool}
            openToolCards={openToolCards}
            onToggleTool={handleToggleTool}
            onToggleCard={toggleToolCard}
            onOpenInstructions={openInstructionsDialog}
          />

          {hasCalendarTools ? (
            <AgentToolCard
              title="Calendar Booking"
              description="Let the agent check availability and book meetings using the Google Calendar of a connected inbox."
              categoryLabel="General"
              tone="general"
              icon={Calendar}
              checked={calendarBookingEnabled}
              onCheckedChange={() => void handleToggleCalendarBooking()}
              expanded={openToolCards.has(CALENDAR_CARD_KEY)}
              onExpandedChange={() => toggleToolCard(CALENDAR_CARD_KEY)}
              disabled={togglingTool !== null}
              isPending={togglingTool === CALENDAR_TOGGLE_KEY}
            >
                {/* Deliverability gate: calendar tools only reach the agent
                    when the SELECTED inbox carries calendar scope. Surface a
                    persistent warning whenever booking is on but that's not
                    yet true (no account / no inbox picked / missing scope) so
                    the requirement is explicit, not just a transient toast. */}
                {calendarBookingEnabled &&
                  !isLoadingCalendar &&
                  !selectedCalendarInbox?.hasCalendarScope && (
                    <Alert className="border-amber-200 bg-amber-50">
                      <Info className="h-4 w-4 text-amber-600" />
                      <AlertDescription className="text-amber-800">
                        {calendarInboxes.length === 0
                          ? "Calendar booking is on, but no Google account is connected. Connect one below — the agent can't book meetings until an inbox with calendar access is selected."
                          : !calendarInboxId
                            ? "Calendar booking is on, but no inbox is selected. Pick an inbox with calendar access below to activate it."
                            : "Calendar booking is on, but the selected inbox doesn't have calendar access yet. Grant access below to activate it."}
                      </AlertDescription>
                    </Alert>
                  )}

                {isLoadingCalendar ? (
              <p className="text-sm text-muted-foreground">Loading calendar status…</p>
            ) : calendarInboxes.length === 0 ? (
              <div className="space-y-3">
                <Alert className="border-amber-200 bg-amber-50">
                  <Info className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-800">
                    No connected Google accounts found. Connect one to give the
                    agent calendar access — you can grant calendar permission in
                    the next step.
                  </AlertDescription>
                </Alert>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    connectGoogle({ onConnected: () => loadCalendarConfig() })
                  }
                  disabled={isConnectingGoogle}
                >
                  {isConnectingGoogle ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Mail className="w-4 h-4 mr-2" />
                  )}
                  <span>Connect a Google account</span>
                </Button>
              </div>
            ) : (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="voice-calendar-inbox">Calendar inbox</Label>
                  <Select
                    value={calendarInboxId ?? ""}
                    onValueChange={handleCalendarInboxChange}
                    disabled={isSavingCalendar || togglingTool !== null}
                  >
                    <SelectTrigger id="voice-calendar-inbox">
                      <SelectValue placeholder="Select an inbox" />
                    </SelectTrigger>
                    <SelectContent>
                      {calendarInboxes.map((inbox) => (
                        <SelectItem key={inbox.id} value={inbox.id}>
                          {inbox.email}
                          <span>{inbox.hasCalendarScope ? "" : " (no calendar access)"}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Calendar tools are delivered to the agent only after an inbox
                    is selected.
                  </p>
                </div>

                {/* Grant calendar access for the selected inbox if missing */}
                {selectedCalendarInbox && !selectedCalendarInbox.hasCalendarScope && (
                  <OAuthScopeConnectionStatus
                    isLoading={false}
                    inboxes={[
                      {
                        id: selectedCalendarInbox.id,
                        email: selectedCalendarInbox.email,
                        hasScope: selectedCalendarInbox.hasCalendarScope,
                      },
                    ]}
                    onConnect={(inboxId) => connectCalendar(inboxId)}
                    icon={Calendar}
                    loadingText="Checking calendar status..."
                    connectButtonText="Grant Access"
                    connectedText="Connected"
                    allConnectedText="Inbox has calendar access"
                  />
                )}

                {/* Connect another Google account (voice has no Inbox tab) */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-muted-foreground"
                  onClick={() =>
                    connectGoogle({ onConnected: () => loadCalendarConfig() })
                  }
                  disabled={isConnectingGoogle}
                >
                  {isConnectingGoogle ? (
                    <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                  ) : (
                    <Mail className="w-3 h-3 mr-1.5" />
                  )}
                  <span>Connect another Google account</span>
                </Button>

                {calendarError && (
                  <Alert className="border-red-200 bg-red-50">
                    <AlertDescription className="text-red-800">
                      {calendarError}
                    </AlertDescription>
                  </Alert>
                )}

                {/* Working hours + meeting defaults (when an inbox is selected) */}
                {calendarConfig && selectedCalendarInbox?.hasCalendarScope && (
                  <Collapsible>
                    <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-muted/50 rounded-lg border hover:bg-muted transition-colors">
                      <span className="text-sm font-medium">
                        Working Hours &amp; Meeting Settings
                      </span>
                      <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="mt-2 space-y-4 p-3 rounded-lg border">
                        <div className="space-y-4">
                          <h4 className="text-sm font-medium">Working Hours</h4>
                          <WorkingHoursConfigEditor
                            value={calendarConfig.working_hours}
                            onChange={(working_hours) =>
                              updateCalendarConfig({ working_hours })
                            }
                            disabled={isSavingCalendar}
                          />
                        </div>
                        <div className="border-t pt-4 space-y-3">
                          <h4 className="text-sm font-medium">Meeting Defaults</h4>
                          <MeetingDefaultsConfig
                            value={calendarConfig.meeting_defaults}
                            onChange={(meeting_defaults) =>
                              updateCalendarConfig({ meeting_defaults })
                            }
                            disabled={isSavingCalendar}
                          />
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )}

                {/* Per-tool instruction editors */}
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Customize how the agent handles calendar requests:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        openInstructionsDialog("check_calendar_availability")
                      }
                      className="text-xs"
                    >
                      <Settings className="w-3 h-3 mr-1.5" />
                      <span>Check Availability</span>
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => openInstructionsDialog("book_calendar_meeting")}
                      className="text-xs"
                    >
                      <Settings className="w-3 h-3 mr-1.5" />
                      <span>Book Meeting</span>
                    </Button>
                  </div>
                </div>
              </>
            )}
            </AgentToolCard>
          ) : null}

          {hasStorefrontAccess && presentShopifyTiles.length > 0 ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 pt-1">
                <ShoppingBag
                  aria-hidden="true"
                  className="h-4 w-4 text-emerald-700"
                />
                <h4 className="text-sm font-semibold">Commerce</h4>
              </div>
              <ShopDomainField
                value={config.shopify_shop_domain}
                onChange={handleShopDomainChange}
                integrations={shopifyIntegrations}
                disabled={isSavingConfig || togglingTool !== null}
                isSaving={isSavingConfig}
              />
              {presentShopifyTiles.map(def => {
                const expanded = openToolCards.has(def.name);
                return (
                  <AgentToolCard
                    key={def.name}
                    title={def.title}
                    description={def.tooltip}
                    categoryLabel="General"
                    tone="general"
                    icon={def.icon}
                    checked={enabledTools.includes(def.name)}
                    onCheckedChange={() => void handleToggleTool(def.name)}
                    expanded={expanded}
                    onExpandedChange={nextExpanded => {
                      if (nextExpanded !== expanded) toggleToolCard(def.name);
                    }}
                    disabled={togglingTool !== null}
                    isPending={togglingTool === def.name}
                    lockedReason={
                      isShopifyTileLocked(def)
                        ? ORDER_LOOKUP_LOCKED_REASON
                        : undefined
                    }
                  >
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={() => openInstructionsDialog(def.name)}
                      disabled={togglingTool !== null || isShopifyTileLocked(def)}
                    >
                      <Settings2 aria-hidden="true" className="mr-2 h-4 w-4" />
                      <span>Customize Instructions</span>
                    </Button>
                  </AgentToolCard>
                );
              })}
              <ShopifyLiveDataNote />
            </div>
          ) : null}
        </AgentToolCategory>
      ) : null}

      {scheduleTools.length > 0 ? (
        <AgentToolCategory
          title="Sending Tools"
          description="Schedule callbacks, WhatsApp messages, and emails for a later time."
          tone="general"
          icon={CalendarClock}
        >
          <VoiceToolCards
            tools={scheduleTools}
            tone="general"
            categoryLabel="Scheduling"
            config={config}
            saveConfig={saveConfig}
            onSettingsSaved={syncSavedSettings}
            togglingTool={togglingTool}
            openToolCards={openToolCards}
            onToggleTool={handleToggleTool}
            onToggleCard={toggleToolCard}
            onOpenInstructions={openInstructionsDialog}
          />
        </AgentToolCategory>
      ) : null}

      {/* Empty state — only when the catalog has no tools at all */}
      {tools.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="py-8">
            <div className="text-center text-muted-foreground">
              <Wrench className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No additional AI tools available</p>
              {user?.isSuperUser && (
                <Button
                  variant="link"
                  className="mt-2"
                  onClick={() =>
                    window.open("/?page=ai-settings&tab=tools", "_blank")
                  }
                >
                  Configure AI Tools
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Manage Tools Link for Super Users */}
      {user?.isSuperUser && tools.length > 0 && (
        <Card className="border-dashed">
          <CardContent className="py-4">
            <Button
              variant="outline"
              className="w-full"
              onClick={() =>
                window.open("/?page=ai-settings&tab=tools", "_blank")
              }
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              <span>Manage AI Tools</span>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Tool Instructions Dialog */}
      {editingToolName && (
        <ToolInstructionsDialog
          open={instructionsDialogOpen}
          onOpenChange={(open) => {
            setInstructionsDialogOpen(open);
            if (!open) {
              setEditingToolName(null);
            }
          }}
          toolName={editingToolName}
          toolDisplayName={getDisplayName(editingToolName)}
          scopeType="voice_agent"
          scopeId={agentId}
          onSave={() => handleInstructionsSaved(editingToolName)}
          onReset={() => handleInstructionsSaved(editingToolName)}
        />
      )}
    </div>
  );
}

function VoiceToolCards({
  agentId,
  tools,
  tone,
  categoryLabel,
  togglingTool,
  openToolCards,
  onToggleTool,
  onToggleCard,
  onOpenInstructions,
  lockedReason,
  config,
  saveConfig,
  onSettingsSaved,
}: VoiceToolCardsProps) {
  return tools.map(tool => {
    const expanded = openToolCards.has(tool.name);
    const setupMessage = !tool.enabled && config
      && (tool.name === "send_email" || tool.name === "send_whatsapp")
      ? getVoiceScheduledSendSetupMessage(config, tool.name)
      : null;
    return (
      <AgentToolCard
        key={tool.name}
        title={getAiToolDisplayName(tool.name, tool.display_name)}
        description={getVoiceToolDescription(tool)}
        categoryLabel={categoryLabel}
        requirement={isDeliveryToolName(tool.name) ? getDeliveryToolRequirement(tool.name) : undefined}
        tone={tone}
        icon={getVoiceToolIcon(tool.name)}
        checked={tool.enabled}
        onCheckedChange={() => void onToggleTool(tool.name)}
        expanded={expanded}
        onExpandedChange={nextExpanded => {
          if (nextExpanded !== expanded) onToggleCard(tool.name);
        }}
        disabled={togglingTool !== null}
        isPending={togglingTool === tool.name}
        lockedReason={lockedReason}
        toggleDisabledReason={setupMessage ?? undefined}
      >
        {tool.name === "schedule_call" && config && saveConfig && onSettingsSaved ? (
          <div className="space-y-3">
          <ToolWorkingHoursSetting id="voice-schedule-call-working-hours"
            checked={config.ai_tools?.tool_settings?.schedule_call?.respect_working_hours === true}
            disabled={togglingTool !== null || Boolean(lockedReason)}
            onCheckedChange={checked => {
              void (async () => {
                const saved = await saveConfig({
                  tools_enabled: { ...config.tools_enabled, schedule_call: true },
                  ai_tools: {
                  ...config.ai_tools,
                  tool_settings: {
                    ...config.ai_tools?.tool_settings,
                    schedule_call: { ...config.ai_tools?.tool_settings?.schedule_call, respect_working_hours: checked },
                  },
                } })
                if (!saved) {
                  toast.error("Could not save callback working-hours setting.")
                  return
                }
                const sync = await onSettingsSaved({ toolName: "schedule_call", enabled: true })
                if (!sync.ok) toast.error(sync.error ?? "Could not sync the callback tool.")
              })()
            }} />
            <Button type="button" variant="outline" disabled={togglingTool !== null || Boolean(lockedReason)}
              onClick={() => { void (async () => {
                if (!await saveConfig({ tools_enabled: { ...config.tools_enabled, schedule_call: false },
                  ai_tools: { ...config.ai_tools, tool_settings: { ...config.ai_tools?.tool_settings, schedule_call: {} } } })) {
                  toast.error("Could not clear callback settings.")
                  return
                }
                const sync = await onSettingsSaved({ toolName: "schedule_call", enabled: false })
                if (!sync.ok) toast.error(sync.error ?? "Could not sync the callback tool.")
              })() }}><span>Clear settings</span></Button>
          </div>
        ) : null}
        {tool.name === "send_whatsapp" && config && saveConfig && onSettingsSaved ? (
          <Suspense fallback={<p className="text-sm text-muted-foreground">Loading WhatsApp settings…</p>}>
            <ScheduleMessageToolSettings config={config} saveConfig={saveConfig} onSaved={onSettingsSaved} />
          </Suspense>
        ) : null}
        {tool.name === "send_email" && config && saveConfig && onSettingsSaved ? (
          <Suspense fallback={<p className="text-sm text-muted-foreground">Loading email settings…</p>}>
            <ScheduleEmailToolSettings settings={config.ai_tools?.tool_settings?.send_email}
              onSave={async settings => {
                const saved = await saveConfig({ tools_enabled: { ...config.tools_enabled, send_email: settings.enabled === true }, ai_tools: {
                  ...config.ai_tools,
                  tool_settings: { ...config.ai_tools?.tool_settings, send_email: settings },
                } })
                if (!saved) throw new Error("Could not save email scheduling settings.")
                const sync = await onSettingsSaved({ toolName: "send_email", enabled: settings.enabled === true })
                if (!sync.ok) throw new Error(`Email settings saved, but Voice tools could not sync: ${sync.error ?? "Try again."}`)
              }} />
          </Suspense>
        ) : null}
        {agentId && tool.name === "delivery_create_shipment" ? (
          <CrmStatusFallbackSettings agentId={agentId} scopeType="voice_agent" disabled={togglingTool !== null || Boolean(lockedReason)} />
        ) : null}
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={() => onOpenInstructions(tool.name)}
          disabled={togglingTool !== null || Boolean(lockedReason)}
        >
          <Settings2 aria-hidden="true" className="mr-2 h-4 w-4" />
          <span>Customize Instructions</span>
        </Button>
        <Alert>
          <Info aria-hidden="true" className="h-4 w-4" />
          <AlertDescription>
            {getVoiceToolInstructionsHint(tool.name)}
          </AlertDescription>
        </Alert>
      </AgentToolCard>
    );
  });
}

function getDeliveryLockedReason(
  settings: ReturnType<typeof useAgentDeliverySettingsQuery>["data"],
): string | undefined {
  if (!settings) return "Delivery settings could not be loaded.";
  if (settings.effectiveBinding) return undefined;
  if (settings.explicitBinding?.state === "disabled") {
    return "Enable deliveries from this agent's Delivery tab first.";
  }
  return "Select an active delivery integration from this agent's Delivery tab first.";
}

function getVoiceToolIcon(_toolName: string): LucideIcon {
  return Wrench;
}

function getVoiceToolDescription(tool: VoiceToolInfo): string {
  return GENERIC_TOOL_UI_DESCRIPTIONS[tool.name] ?? tool.description;
}

function getVoiceToolInstructionsHint(toolName: string): string {
  switch (toolName) {
    case "capture_customer_info":
      return "Configure when AI captures information that identifies a customer and connects their conversations.";
    default:
      return "Customize how AI uses this tool during conversations.";
  }
}
